import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@core/services/api/apiRequest";

/**
 * System variable response type
 */
export type SystemVariableResponse = {
  key: string;
  name: string;
  value: string;
  description: string;
};

/**
 * Hook to fetch a system variable by key.
 * Returns React Query result - the source of truth for system variable data.
 *
 * @param key - The system variable key to fetch
 * @param options - Additional query options
 *
 * @example
 * ```typescript
 * const { data, isLoading } = useSystemVariable('marketing_site_mode');
 *
 * if (data?.value === 'closed') {
 *   // Hide signup CTAs
 * }
 * ```
 */
export function useSystemVariable(
  key: string,
  options?: {
    enabled?: boolean;
  }
) {
  const { enabled = true } = options ?? {};

  return useQuery<SystemVariableResponse, Error>({
    queryKey: ["system-variable", key],
    queryFn: async () => {
      return apiRequest(
        `/system-variables/${key}/`,
        "GET",
        null,
        false // No auth required - public endpoint
      ) as Promise<SystemVariableResponse>;
    },
    enabled,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    retry: 1, // Only retry once on failure
  });
}

export default useSystemVariable;
